export const TOKEN_INVALID_CODE = "TOKEN_INVALID";

export const isTerminalAuthError = (error) =>
    error?.response?.status === 401 && error?.response?.data?.code === TOKEN_INVALID_CODE;

/** `api/apiURLs.js#getProfile`, without its slashes or a query string. */
const PROFILE_PATH = "user/profile";

const normalizePath = (url) =>
    String(url || "")
        .split("?")[0]
        .replace(/^https?:\/\/[^/]+/, "")
        .replace(/^\/+|\/+$/g, "");

/**
 * The two replies GET /user/profile gives for a token the server cannot use.
 *
 * THE PROFILE ROUTE DOES NOT ANSWER 401, ON PURPOSE (api controllers/user.js,
 * getUserByToken). A 401 there logged the customer app's users out in bulk on
 * 2026-08-05, so the server keeps:
 *   - 404 `{ message: "notfound" }`    — the token has no `user_tokens` row;
 *   - 500 `{ message: "unauthorized" }` — no token at all.
 *
 * The dashboard read both as "the server is down", so it never refreshed and
 * never logged out, and a user whose access token was deleted stayed on
 * «تعذر تحميل الحساب» for good. That happens on every refresh in another tab:
 * rotation deletes the old access-token row (util/refreshTokens.js).
 *
 * Profile route only. Any other 404 or 500 is a real error, not a dead session.
 *
 * @param {import("axios").AxiosError} error
 * @returns {boolean}
 */
export const isDeadProfileSessionError = (error) => {
    if (normalizePath(error?.config?.url) !== PROFILE_PATH) return false;

    const status = error?.response?.status;
    // `rawMessage` is the API's own text. For a 5xx, `message` also has the
    // reference that the dashboard shows (api/index.js).
    const message = error?.response?.data?.rawMessage ?? error?.response?.data?.message;
    return (
        (status === 404 && message === "notfound") || (status === 500 && message === "unauthorized")
    );
};

/**
 * The session is dead: refresh once, then log out if that fails too. The
 * API's 401 TOKEN_INVALID contract, plus the profile route's two replies above.
 * Network errors, 429 and every other 5xx are NOT dead sessions: they keep the
 * credentials and offer a retry.
 *
 * @param {import("axios").AxiosError} error
 * @returns {boolean}
 */
export const isDeadSessionError = (error) =>
    isTerminalAuthError(error) || isDeadProfileSessionError(error);

export const getRequestBearerToken = (config) => {
    const headers = config?.headers;
    const authorization =
        (typeof headers?.get === "function" && headers.get("Authorization")) ||
        headers?.Authorization ||
        headers?.authorization;

    if (typeof authorization !== "string") return null;
    const match = authorization.match(/^\S+\s+(.+)$/);
    return match?.[1] || null;
};

export const setRequestBearerToken = (config, token) => {
    config.headers = config.headers || {};
    if (typeof config.headers.set === "function") {
        config.headers.set("Authorization", `Bearer ${token}`);
    } else {
        config.headers.Authorization = `Bearer ${token}`;
    }
};

/**
 * Decode only the public JWT expiry claim. This is scheduling, not
 * authentication — the API still verifies the signature and database row.
 */
export const isJwtExpiredOrExpiring = (token, skewMs = 5_000) => {
    try {
        const payloadPart = token?.split(".")?.[1];
        if (!payloadPart) return false;
        const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
        const payload = JSON.parse(window.atob(padded));
        return Number.isFinite(payload.exp) && payload.exp * 1000 <= Date.now() + skewMs;
    } catch {
        // A malformed token will be rejected by the API. Do not turn a local
        // decode problem into a refresh loop.
        return false;
    }
};
